import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import ToolLogo from '../ui/ToolLogo';
import { LogoSlug } from '../../data/logos';

type Accent = 'cyan' | 'magenta' | 'green' | 'purple' | 'amber';

const accentColor: Record<Accent, string> = {
  cyan: '#22e6ff',
  magenta: '#ff3df0',
  green: '#2bffb0',
  purple: '#9d5cff',
  amber: '#ffb13d',
};

interface Niche {
  name: string;
  tag: string;
  accent: Accent;
  icon: React.ReactNode;
  /** The pain the automation removes, in the operator's words. */
  problem: string;
  /** Concrete systems I've shipped for this vertical. */
  builds: string[];
  /** Real brands I've done this work for. */
  clients: string[];
  tools: LogoSlug[];
}

const niches: Niche[] = [
  {
    name: 'Real Estate & Property',
    tag: 'proptech',
    accent: 'cyan',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    problem:
      'Seller leads go cold in minutes and comps live in twelve browser tabs.',
    builds: [
      'Missed-call speed-to-lead SMS text-back',
      'Zillow / Apify property-data ETL',
      'Cash-offer & investor pipelines in GoHighLevel',
      'CRM ↔ Google Sheets reporting dashboards',
    ],
    clients: [
      'BrightLane Property',
      'SharpEdge Property',
      'Axiom Realty Solutions',
      'OnOffDeals',
    ],
    tools: ['gohighlevel', 'n8n', 'twilio', 'selenium', 'googlesheets', 'python'],
  },
  {
    name: 'Home Services & Trades',
    tag: 'field-ops',
    accent: 'green',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M14.5 3.5a4.5 4.5 0 0 0-5.9 5.6L3.6 14.1a2 2 0 1 0 2.8 2.8l5-5a4.5 4.5 0 0 0 5.6-5.9l-2.6 2.6-2.1-2.1 2.2-2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    problem:
      'The phone rings while the crew is on a roof — and that job is gone.',
    builds: [
      'IVR call routing with an AI voice receptionist',
      'Quote-request intake → instant SMS confirmation',
      'Job reminders, dispatch alerts & review requests',
      'Estimate follow-up sequences that reheat old quotes',
    ],
    clients: [
      'One Stop Redding Junk Removal',
      'Shasta Brush Clearing',
      'USA Roofing Supply',
    ],
    tools: ['gohighlevel', 'twilio', 'n8n', 'calendly', 'whatsapp', 'elevenlabs'],
  },
  {
    name: 'Solar & Energy',
    tag: 'high-ticket',
    accent: 'amber',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    problem:
      'Expensive paid clicks land in an inbox nobody checks until Monday.',
    builds: [
      'Meta / Google Ads → CRM with closed-loop attribution',
      'Instant qualification + appointment setting',
      'Proposal follow-up and no-show recovery',
      'Cost-per-booked-consult reporting',
    ],
    clients: ['PowerSmith Solar'],
    tools: ['meta', 'googleads', 'gohighlevel', 'n8n', 'googleanalytics'],
  },
  {
    name: 'SaaS & Product Teams',
    tag: 'saas-ops',
    accent: 'purple',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 20h8M12 17v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7.5 9.5 9.5 11l-2 1.5M12 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    problem:
      'Billing, usage limits and onboarding are held together by manual checks.',
    builds: [
      'Stripe subscriptions, trials, coupons & usage metering',
      'Webhook-driven state machines with retries',
      'Async FastAPI services over MongoDB + Redis',
      'LLM features with guardrails and output validation',
    ],
    clients: ['OnOffDeals', 'Podiam AI', 'GeniOS', 'AI Impulse'],
    tools: ['stripe', 'fastapi', 'python', 'mongodb', 'redis', 'anthropic'],
  },
  {
    name: 'Healthcare & Medical AI',
    tag: 'clinical',
    accent: 'cyan',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3.5" y="6" width="17" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 10v5M9.5 12.5h5M9 6V4.5h6V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    problem:
      'Imaging and patient data pipelines are still stitched together by hand.',
    builds: [
      'AI DICOM processing for biomedical imaging',
      'Real-time facial recognition at 95%+ accuracy',
      'Clinician & clinic data extraction at scale',
      'Containerised inference services',
    ],
    clients: ['Trinzz'],
    tools: ['python', 'opencv', 'tensorflow', 'docker', 'selenium'],
  },
  {
    name: 'Legal, Finance & Documents',
    tag: 'docflow',
    accent: 'amber',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14 3v4h4M8.5 12h7M8.5 16h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    problem:
      'Statements, contracts and reports get re-typed by a human every month.',
    builds: [
      'PDF bank statements → clean CSV with AI table extraction',
      'Serverless LaTeX / DOCX document pipelines',
      'RAG legal & policy research assistants',
      'E-signature routing with DocuSign',
    ],
    clients: ['Podiam AI', 'LegalEase AI', 'PDF2CSV-AI'],
    tools: ['python', 'anthropic', 'langchain', 'flask', 'huggingface'],
  },
  {
    name: 'Agencies & Consulting',
    tag: 'white-label',
    accent: 'magenta',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="9" r="3" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="16.5" cy="10.5" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 19c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5M14 19c0-2.2 1.4-3.6 3.5-3.6S21 16.8 21 19" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    problem:
      'Every new client account is rebuilt from scratch, then reported on manually.',
    builds: [
      'Reusable GoHighLevel snapshots & sub-account rollouts',
      'Multi-client reporting dashboards',
      'Lead routing and round-robin assignment',
      'Slack / email alerting on pipeline events',
    ],
    clients: ['Charyx Labs', 'Axiom Realty Solutions'],
    tools: ['gohighlevel', 'slack', 'googleanalytics', 'googlesheets', 'n8n', 'notion'],
  },
  {
    name: 'Education & Learning',
    tag: 'edtech',
    accent: 'green',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 4 2.5 8.5 12 13l9.5-4.5L12 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M6.5 10.8V15c0 1.5 2.5 2.8 5.5 2.8s5.5-1.3 5.5-2.8v-4.2M21 9v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    problem:
      'Enrolment, progress nudges and parent updates eat the whole week.',
    builds: [
      'Student onboarding & enrolment funnels',
      'Progress and streak notification jobs',
      'Content generation and publishing pipelines',
      'Cohort reporting for parents and tutors',
    ],
    clients: ['Gurukin'],
    tools: ['react', 'supabase', 'n8n', 'gmail', 'openai'],
  },
];

const FILTERS = ['All', 'Lead-gen', 'Ops & data', 'Product'] as const;
type Filter = (typeof FILTERS)[number];

const filterMap: Record<Filter, string[]> = {
  All: [],
  'Lead-gen': [
    'Real Estate & Property',
    'Home Services & Trades',
    'Solar & Energy',
    'Agencies & Consulting',
  ],
  'Ops & data': [
    'Legal, Finance & Documents',
    'Healthcare & Medical AI',
    'Real Estate & Property',
  ],
  Product: ['SaaS & Product Teams', 'Education & Learning', 'Healthcare & Medical AI'],
};

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const NichesSection: React.FC = () => {
  const { themeConfig } = useTheme();
  const [filter, setFilter] = useState<Filter>('All');

  const visible = useMemo(() => {
    const allow = filterMap[filter];
    return allow.length ? niches.filter((n) => allow.includes(n.name)) : niches;
  }, [filter]);

  return (
    <section id="niches" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-10 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-amber/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-amber">
            {'// who i build for'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          Eight niches, one <span className="gradient-text">playbook</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Every industry has the same three leaks: leads that go unanswered, data
          that gets re-typed, and follow-up that never happens. Here is what I
          plug them with — and who I&apos;ve already done it for.
        </p>
      </motion.div>

      {/* filter rail */}
      <div className="mb-8 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const active = f === filter;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={active}
              className={`rounded-lg border px-3 py-1.5 font-mono text-xs transition-all ${
                active
                  ? 'border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan'
                  : 'border-canvas-border bg-canvas-node text-ink-soft hover:border-neon-cyan/40 hover:text-ink-main'
              }`}
            >
              {f.toLowerCase()}
            </button>
          );
        })}
        <span className="ml-auto font-mono text-xs text-ink-muted">
          {visible.length} / {niches.length} shown
        </span>
      </div>

      <motion.div
        key={filter}
        className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {visible.map((n) => {
          const c = accentColor[n.accent];
          return (
            <motion.article
              key={n.name}
              variants={item}
              className="n8n-node flex h-full flex-col p-5"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span
                    className="node-chip !h-11 !w-11 shrink-0"
                    style={{
                      color: c,
                      borderColor: `${c}59`,
                      background: `linear-gradient(135deg, ${c}2e, rgba(157,92,255,0.14))`,
                      boxShadow: `0 0 22px -8px ${c}`,
                    }}
                  >
                    {n.icon}
                  </span>
                  <h3 className="font-display text-lg font-semibold leading-tight text-ink-main">
                    {n.name}
                  </h3>
                </div>
                <span
                  className="shrink-0 rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: c, border: `1px solid ${c}59`, background: `${c}14` }}
                >
                  {n.tag}
                </span>
              </div>

              <p className="mb-4 border-l-2 pl-3 text-sm italic leading-relaxed text-ink-soft"
                style={{ borderColor: `${c}66` }}
              >
                {n.problem}
              </p>

              <ul className="mb-5 space-y-1.5">
                {n.builds.map((b) => (
                  <li key={b} className="flex gap-2 text-sm leading-snug text-ink-soft">
                    <span className="mt-[3px] shrink-0" style={{ color: c }}>
                      ▹
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mb-4">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                  built for
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {n.clients.map((client) => (
                    <span
                      key={client}
                      className="rounded-md border bg-canvas-deep/60 px-2 py-1 font-mono text-[11px] text-ink-soft"
                      style={{ borderColor: `${c}33` }}
                    >
                      {client}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-auto flex flex-wrap gap-1.5 border-t border-canvas-border/60 pt-4">
                {n.tools.map((t) => (
                  <ToolLogo key={t} slug={t} showLabel={false} size={18} />
                ))}
              </div>
            </motion.article>
          );
        })}
      </motion.div>

      <p className="mt-8 text-center font-mono text-xs text-ink-muted">
        Not on the list?{' '}
        <a
          href="https://calendly.com/sharmachaitanya945/meeting-with-me"
          target="_blank"
          rel="noopener noreferrer"
          className="text-neon-cyan hover:underline"
        >
          The playbook ports — book 30 minutes →
        </a>
      </p>
    </section>
  );
};

export default NichesSection;
